import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AppLayout ,StatusIndicator} from "@cloudscape-design/components";
import TopNavigation from "./containers/TopNavigation/TopNavigation";
import Sidebar from "./containers/Sidebar/Sidebar";
import RightSidebar from "./containers/RightSidebar/RightSidebar";
import { applyTheme, getCurrentTheme, Theme } from "./themes/themeConfig";
import "./themes/global.themed.css";
import "./themes/navigation-force.css";
import "./App.css";
import HomePage from "./containers/homePage/HomePage";
import NoPermission from "./components/NoPermission/NoPermission";
import NotFound from "./components/NotFound/NotFound";
import SignedOut from "./components/SignedOut/SignedOut";
import ROUTE_CONFIG, { SystemType } from "./config/routeConfig";
import { getPermissionMeRequest } from "./redux/actions/permissionActions";
import { SYSTEM } from "./containers/PermissionManagement/constants";
import { usePermission } from "./hooks/usePermission";
import { VIEW_ACTION_MAP } from "./config/permissionConfig";
import { CommonContext } from "./types/commonContext";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./redux/reducers/rootReducer";
import { config } from "./config/config";
import { PermissionProvider } from "./contexts/PermissionProvider";
import Portal from "./containers/Portal/Portal";
import { isPortalHost } from "./config/domain";

const ProtectedRoute: React.FC<{ featureKey: string; system?: SystemType; children: React.ReactElement }> = ({
  featureKey,
  system,
  children,
}) => {
  const { hasPermission } = usePermission();
  const meStatus = useSelector((state: RootState) => state.permission.me.status);
  const meData = useSelector((state: RootState) => state.permission.me.data);
  if ((meStatus === "loading" || meStatus === "idle") && !meData) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", padding: "40px" }}>
        <StatusIndicator type="loading">Loading permissions...</StatusIndicator>
      </div>
    );
  }
  if (hasPermission(featureKey, system)) return children;
  return <NoPermission />;
};

// Build common context to pass to child package components
const useCommonContext = (): CommonContext => {
  const userState = useSelector((state: RootState) => state.user);
  return {
    userName: userState.userName,
    email: userState.email,
    familyName: userState.familyName,
    givenName: userState.givenName,
    provider: userState.provider,
    idToken: userState.token,
    accessToken: '',
    refreshToken: '',
    tokenExpiration: userState.expiration,
    baseUrl: config.BASE_URL,
  };
};

// App Content Component with Router
const AppContent: React.FC = () => {
  const location = useLocation();
  const common = useCommonContext();
  const [chatState, setChatState] = useState<"closed" | "default" | "expanded">(
    location.pathname === "/analysis" || location.pathname === "/reports"
      ? "expanded"
      : "closed",
  );
  const [navigationOpen, setNavigationOpen] = useState(true);
  const [currentTheme, setCurrentTheme] = useState<Theme>(getCurrentTheme());
  const prevPathRef = React.useRef<string>(location.pathname);

  const handleChatStateChange = (
    newState: "closed" | "default" | "expanded",
  ) => {
    setChatState(newState);
  };

  useEffect(() => {
    const updateTheme = () => {
      const theme = getCurrentTheme();
      setCurrentTheme(theme);
      applyTheme(theme);

      document.body.style.backgroundColor = theme.colors.mainBackground;
      document.body.style.color = theme.colors.textPrimary;
    };

    window.addEventListener("themeChanged", updateTheme);
    updateTheme();

    return () => {
      window.removeEventListener("themeChanged", updateTheme);
    };
  }, []);

  // Auto-expand chatbot on specific routes
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;

      if (
        location.pathname === "/analysis" ||
        location.pathname === "/reports"
      ) {
        setChatState("expanded");
      } else if (location.pathname === "/optimization" && chatState === "expanded") {
        setChatState("default");
      } else {
        setChatState("closed");
      }
    }
  }, [location.pathname]);

  return (
    <div
      className="awsui"
      style={{
        backgroundColor: currentTheme.colors.mainBackground,
        color: currentTheme.colors.textPrimary,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}
      >
        <TopNavigation />
      </div>

      <div
        id="app-main-scroll-container"
        style={{
          display: "flex",
          flex: 1,
          overflow: "auto",
          position: "relative",
          marginTop: "56px",
        }}
      >
        <div
          style={{
            flex: 1,
            transition: "margin-right 0.3s ease-out",
            marginRight:
              chatState === "closed"
                ? "50px"
                : chatState === "default"
                  ? "500px"
                  : "0px",
          }}
        >
          <AppLayout
            navigation={<Sidebar />}
            navigationOpen={navigationOpen}
            onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
            contentType="table"
            toolsHide={true}
            content={
              <div
                style={{
                  backgroundColor: currentTheme.colors.cardBackground,
                  color: currentTheme.colors.textPrimary,
                  minHeight: "100%",
                  width: "100%",
                  transition: "width 0.3s ease-out",
                  overflow: "visible",
                }}
              >
                <PermissionProvider>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  {ROUTE_CONFIG.map((route) => {
                    const el = typeof route.element === "function"
                      ? route.element(common)
                      : route.element;
                    return (
                      <Route
                        key={route.id}
                        path={route.href}
                        element={
                          <ProtectedRoute featureKey={`${route.id}.${VIEW_ACTION_MAP[route.id]}`} system={route.system}>
                            {el as React.ReactElement}
                          </ProtectedRoute>
                        }
                      />
                    );
                  })}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </PermissionProvider>
              </div>
            }
          />
        </div>

        <RightSidebar
          chatState={chatState}
          onChatStateChange={handleChatStateChange}
          navigationOpen={navigationOpen}
        />
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const dispatch = useDispatch();
  // Portal host (goh.lab126.amazon.dev, no system prefix) is the public system
  // entry page. It has no single "current system", so it must NOT request
  // permissions or enter the system layout — it just renders the portal cards.
  const portal = React.useMemo(() => isPortalHost(), []);
  const [authState, setAuthState] = useState<string>(() => {
    if (sessionStorage.getItem('msa_signed_out') === 'true') {
      return "SIGNED_OUT";
    }
    return "UNAUTHENTICATED";
  });
  const hasInitialized = React.useRef<boolean>(false);

  useEffect(() => {
    // Only system hosts fetch permissions; the portal has no current system.
    if (!portal) {
      dispatch(getPermissionMeRequest(SYSTEM));
    }
    // Prevent double execution in React Strict Mode
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    // If user just signed out, don't attempt sign-in
    if (authState === "SIGNED_OUT") {
      return;
    }

    // Initialize theme system on app startup
    const currentTheme = getCurrentTheme();
    applyTheme(currentTheme);
    document.body.style.backgroundColor = currentTheme.colors.mainBackground;
    document.body.style.color = currentTheme.colors.textPrimary;
    document.body.style.fontFamily = currentTheme.colors.textPrimary
      ? "Amazon Ember, Helvetica Neue, Roboto, Arial, sans-serif"
      : "inherit";
    }, []);

  if (authState === "SIGNED_OUT") {
    return <SignedOut />;
  }

  // Portal host: render the entry page (top navigation + system cards) without
  // the system Sidebar/AppLayout. Login is NOT enforced here — an unauthenticated
  // user sees the portal with a "Log in" action in the top navigation; clicking a
  // system card takes them to that system's domain, which handles its own login.
  if (portal) {
    return (
      <div className="app-shell">
        <div className="app-top-nav">
          <TopNavigation />
        </div>
        <div className="app-portal-content">
          <Portal />
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
